import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from './Image';
import useImageViewerStore from '../../store/ImageViewerStore';

function ImageViewer() {
  const {
    isViewerOpen,
    currentImageUrl,
    currentImageAlt,
    images,
    currentIndex,
    setIsViewerOpen,
    goToNext,
    goToPrevious,
  } = useImageViewerStore();

  const handleClose = useCallback(() => setIsViewerOpen(false), [setIsViewerOpen]);

  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowRight' && hasMultipleImages) {
        goToNext();
      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {
        goToPrevious();
      }
    };
    if (isViewerOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isViewerOpen, handleClose, goToNext, goToPrevious, hasMultipleImages]);

  const ext = currentImageUrl.split('.').pop()?.split('?')[0]?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
  const isVideo = ['mp4', 'webm'].includes(ext || '');

  return createPortal(
    <AnimatePresence initial={false}>
      {isViewerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed top-0 bottom-0 left-0 right-0 flex items-center justify-center w-full h-full bg-black/80 z-[999999] backdrop-blur-sm"
          onClick={handleClose}
        >
          <div className="relative flex items-center justify-center w-full h-full p-5 select-none">
            <button type="button" onClick={handleClose} className="absolute right-8 top-8 z-10">
              <IconX size={35} className="text-white hover:text-gray-300 transition-colors" />
            </button>

            {/* Navigation Arrows */}
            {hasMultipleImages && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  className="absolute left-8 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm transition-all"
                  aria-label="Previous image"
                >
                  <IconChevronLeft size={32} className="text-white" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  className="absolute right-8 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-3 backdrop-blur-sm transition-all"
                  aria-label="Next image"
                >
                  <IconChevronRight size={32} className="text-white" />
                </button>
              </>
            )}

            {isImage && (
              <div className="flex flex-col items-center gap-3">
                <motion.div
                  key={currentImageUrl}
                  initial={{ scale: 0.9, filter: 'blur(10px)' }}
                  animate={{ scale: 1, filter: 'blur(0px)' }}
                  exit={{ scale: 0.9, filter: 'blur(10px)' }}
                  transition={{ duration: 0.2 }}
                >
                  <Image
                    src={currentImageUrl}
                    alt={currentImageAlt}
                    className="max-h-[85vh] max-w-[85vw] w-auto h-auto object-contain rounded-lg shadow-2xl cursor-zoom-out"
                  />
                </motion.div>

                {/* Label and Counter */}
                {(images[currentIndex]?.label || hasMultipleImages) && (
                  <div className="flex items-center gap-1 p-1 bg-white rounded-lg">
                    {images[currentIndex]?.label && (
                      <>
                        <div className="rounded-lg px-4 py-2 h-9 flex items-center">
                          <div className="text-gray-800 text-sm italic">
                            {images[currentIndex].label}
                          </div>
                        </div>
                        {hasMultipleImages && <div className="text-gray-200">|</div>}
                      </>
                    )}
                    {hasMultipleImages && (
                      <div className="bg-white rounded-lg px-3 py-2 h-9 flex items-center">
                        <div className="text-gray-600 text-xs">
                          {currentIndex + 1} / {images.length}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {isVideo && (
              <div className="flex flex-col items-center gap-3">
                <motion.div
                  key={currentImageUrl}
                  initial={{ scale: 0.9, filter: 'blur(10px)' }}
                  animate={{ scale: 1, filter: 'blur(0px)' }}
                  exit={{ scale: 0.9, filter: 'blur(10px)' }}
                  transition={{ duration: 0.2 }}
                >
                  <video
                    controls
                    className="max-h-[80vh] max-w-[80vw] w-auto h-auto object-contain rounded-lg shadow-2xl"
                  >
                    <source src={currentImageUrl} type={`video/${ext}`} />
                    <track kind="captions" srcLang="en" label="English captions" />
                    Your browser does not support the video tag.
                  </video>
                </motion.div>

                {/* Label and Counter */}
                {(images[currentIndex]?.label || hasMultipleImages) && (
                  <div className="flex items-center gap-1 p-1 bg-white rounded-lg">
                    {images[currentIndex]?.label && (
                      <>
                        <div className="rounded-lg px-4 py-2 h-9 flex items-center">
                          <div className="text-gray-800 text-sm italic">
                            {images[currentIndex].label}
                          </div>
                        </div>
                        {hasMultipleImages && <div className="text-gray-200">|</div>}
                      </>
                    )}
                    {hasMultipleImages && (
                      <div className="bg-white rounded-lg px-3 py-2 h-9 flex items-center">
                        <div className="text-gray-600 text-xs">
                          {currentIndex + 1} / {images.length}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export default ImageViewer;
